import { useQueryClient } from "@tanstack/react-query";
import { Square } from "lucide-react";
import { stopGenerationAndReconcile } from "../../api/stopReconciliation";
import QUERYKEYS from "../../api/queryKeys";
import { useSparkContext } from "../../context/SparkContext";
import useGenerateApiSignal from "../../store/useGenerateApiSignal";
import useStreamingStateStore from "../../store/useStreamingStateStore";
import ChatStore from "../../store/chatStore";

const StopGenerationButton = ({ setGenerateStatus, setRegenerateIndex }) => {
  const { chat_id, space_id } = useSparkContext();
  const queryClient = useQueryClient();

  const handleStop = async () => {
    const chatSignal = useGenerateApiSignal.getState().signals[chat_id] || {};
    const { signal, generate_id, generation_id } = chatSignal;
    const streamingState = useStreamingStateStore.getState();
    const pendingGenerationId = generate_id
      ? streamingState.pendingGenerations[generate_id]?.generation_id ||
        streamingState.activeStreams[generate_id]?.generation_id
      : null;
    const answerId = generation_id || pendingGenerationId;

    try {
      // Mark the message as user-stopped BEFORE aborting so the Firestore
      // real-time listener knows to skip the incoming complete answer from
      // the backend (which keeps generating after the client disconnects).
      if (generate_id) {
        const streamingStore = useStreamingStateStore.getState();
        streamingStore.markUserStopped(generate_id);
        streamingStore.markStopping(generate_id);

        const reconcileTerminal = () => {
          useStreamingStateStore.getState().clearStopping(generate_id);
          queryClient.invalidateQueries({
            queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space_id],
          });
        };

        try {
          if (!answerId) throw new Error("generation ID is unavailable");
          await stopGenerationAndReconcile({
            chatId: chat_id,
            spaceId: space_id,
            answerId,
            generationId: answerId,
            onTerminal: reconcileTerminal,
          });
        } catch {
          // The stop request can legitimately fail with a 409 when the
          // generation already reached a terminal state (e.g. a fast answer
          // completed the instant before Stop was clicked) — not a reason to
          // leave the prompt/canvas stuck. The outer finally below still
          // runs; only undo the optimistic user-stopped flag here so the
          // real completed answer isn't suppressed.
          useStreamingStateStore.getState().clearUserStopped(generate_id);
          useStreamingStateStore.getState().clearStopping(generate_id);
          queryClient.invalidateQueries({
            queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space_id],
          });
        }
      }
    } finally {
      // Must always run — on success, on a failed/409 stop request, and even
      // when there was no generate_id — otherwise the prompt textarea/Edit
      // buttons and the Nebula canvas (gated on isLatestMessageLoading) can
      // stay stuck in the generating state.
      signal?.abort();
      useGenerateApiSignal.getState().removeSignal(chat_id);
      setGenerateStatus(false);
      setRegenerateIndex?.(null);
      ChatStore.getState().setIsLatestMessageLoading(false);
    }
  };

  return (
    <button onClick={handleStop} aria-label="Stop generation" className="photon-primary-action rounded-full transition-colors">
      <div
        className="opacity-100 rounded-full w-9 h-9 flex items-center justify-center"
      >
        <Square className="w-4 h-4 fill-current stroke-none" />
      </div>
    </button>
  );
};

export default StopGenerationButton;
